import { Checkbox, ListItemButton, ListItemText } from "@mui/material";
import useDebouncedSearchTerm from "hooks/strings/useDebouncedSearchTerm";
import useGetCrews from "hooks/crews/useGetCrews";
import { useState } from "react";
import { centsToDollarsInput, WorksheetLibrarySelection } from "../worksheetFormValues";
import MultiSelectFooter from "./MultiSelectFooter";
import PickerList from "./PickerList";
import PickerSearch from "./PickerSearch";
import LoadMoreButton from "./LoadMoreButton";
import { PickerProps } from "./types";

export default function CrewPicker({ onClose, onSelect, onSelectMany }: PickerProps) {
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useDebouncedSearchTerm("");
  const { data, loading, paginatorInfo, fetchMore } = useGetCrews({
    first: 50,
    search: debouncedSearchTerm || undefined,
  });
  const crews = data ?? [];
  const [selected, setSelected] = useState<Record<string, WorksheetLibrarySelection>>({});

  const selectionFor = (crew: (typeof crews)[number]): WorksheetLibrarySelection => ({
    sourceType: "crew",
    sourceId: crew.id,
    sourceName: crew.name,
    name: crew.name,
    rate: centsToDollarsInput(crew.rate ?? 0),
    unit: "HOURS",
    quantity: "1",
  });

  return (
    <PickerList
      title="Select Crew"
      loading={loading}
      empty={crews.length === 0}
      header={
        <PickerSearch value={searchTerm} placeholder="Search crews" onChange={setSearchTerm} />
      }
      onClose={onClose}
      footer={
        <MultiSelectFooter
          selections={Object.values(selected)}
          onSelectMany={onSelectMany}
          onClose={onClose}
        />
      }
    >
      {crews.map((crew) => {
        const selection = selectionFor(crew);

        return (
          <ListItemButton
            key={crew.id}
            selected={!!selected[crew.id]}
            onClick={() => {
              if (onSelectMany) {
                setSelected((previous) => {
                  const next = { ...previous };
                  if (next[crew.id]) {
                    delete next[crew.id];
                  } else {
                    next[crew.id] = selection;
                  }
                  return next;
                });
                return;
              }
              onSelect(selection);
              onClose();
            }}
          >
            {onSelectMany && <Checkbox checked={!!selected[crew.id]} size="small" />}
            <ListItemText primary={crew.name} />
          </ListItemButton>
        );
      })}
      {paginatorInfo?.hasMorePages && (
        <LoadMoreButton
          label="Load more crews"
          onClick={() =>
            fetchMore({
              page: (paginatorInfo.currentPage ?? 1) + 1,
              updateQuery: (previous, { fetchMoreResult }) => {
                if (!fetchMoreResult?.getCrews) {
                  return previous;
                }

                return {
                  ...fetchMoreResult,
                  getCrews: {
                    ...fetchMoreResult.getCrews,
                    data: [...(previous.getCrews?.data ?? []), ...fetchMoreResult.getCrews.data],
                  },
                };
              },
            })
          }
        />
      )}
    </PickerList>
  );
}
